import type { EnrichedTableField } from '../types/metadata';
import type { FilterCondition } from '../types/filters';
import type { QueryConditions } from '../types/supabase';
import { isValidEmail, isValidPhone, normalizeEmail, normalizePhone } from './searchNormalization';

export interface BuildTableSearchConditionsParams {
  searchTerm: string;
  fields?: EnrichedTableField[];
}

/**
 * Builds the shared indexed search path used by both global search and table grids.
 * Exact normalized phone/email matches are ORed with ordinary full-text search.
 */
export function buildTableSearchConditions({
  searchTerm,
  fields,
}: BuildTableSearchConditionsParams): QueryConditions {
  let hasFullTextSearchFields = false;
  const emailSearchFields: string[] = [];
  const phoneSearchFields: string[] = [];
  const conditions: FilterCondition[] = [];

  if (fields && Array.isArray(fields)) {
    const searchableFields = fields.filter((field) => field.is_searchable);
    if (searchableFields.length === 0) hasFullTextSearchFields = true;
    searchableFields.forEach((field) => {
      const uiData = field.ui_data;
      const searchField = uiData && typeof uiData === 'object' && !Array.isArray(uiData)
        ? uiData.search_field
        : null;
      if (field.is_email && typeof searchField === 'string') {
        emailSearchFields.push(searchField);
      } else if (field.is_phone && typeof searchField === 'string') {
        phoneSearchFields.push(searchField);
      } else {
        hasFullTextSearchFields = true;
      }
    });
  } else {
    hasFullTextSearchFields = true;
  }

  if (isValidEmail(searchTerm)) {
    const normalizedEmail = normalizeEmail(searchTerm);
    emailSearchFields.forEach((searchField) => conditions.push({
      filterType: 'text',
      type: 'equals',
      colId: searchField,
      filter: normalizedEmail,
    }));
  }

  if (isValidPhone(searchTerm)) {
    const normalizedPhone = normalizePhone(searchTerm);
    phoneSearchFields.forEach((searchField) => conditions.push({
      filterType: 'text',
      type: 'equals',
      colId: searchField,
      filter: normalizedPhone,
    }));
  }

  if (hasFullTextSearchFields) {
    conditions.push({
      filterType: 'text',
      type: 'plain_fts',
      colId: 'full_text_search',
      filter: searchTerm,
    });
  }

  if (conditions.length === 0) return {};
  return {
    searchQuery: {
      filterType: 'join',
      type: 'OR',
      conditions,
    },
  };
}
